/**
 * Internal handling state the backend keeps on every ICS2 notification, independent of the
 * declaration outcome. A `SUPERSEDED` notification belongs to a submission a newer one overtook, so
 * it must not be presented as the current state of the declaration.
 */
export enum ICS2NotificationProcessingStatus {
    AWAITING_CLASSIFICATION = 'AWAITING_CLASSIFICATION',
    AWAITING_CORRELATION = 'AWAITING_CORRELATION',
    PROCESSED = 'PROCESSED',
    SUPERSEDED = 'SUPERSEDED',
    REQUIRES_RECONCILIATION = 'REQUIRES_RECONCILIATION',
}

/** Which exchange with customs a notification answers - the same message type can answer several. */
export enum ICS2TransactionType {
    SUBMISSION = 'SUBMISSION',
    AMENDMENT = 'AMENDMENT',
    ADDITIONAL_INFORMATION = 'ADDITIONAL_INFORMATION',
    HRCM_RESPONSE = 'HRCM_RESPONSE',
    INVALIDATE = 'INVALIDATE',
}

export const ics2TransactionTypeLabels: Record<ICS2TransactionType, string> = {
    [ICS2TransactionType.SUBMISSION]: 'Submission',
    [ICS2TransactionType.AMENDMENT]: 'Amendment',
    [ICS2TransactionType.ADDITIONAL_INFORMATION]: 'Additional information response',
    [ICS2TransactionType.HRCM_RESPONSE]: 'High Risk Cargo & Mail screening response',
    [ICS2TransactionType.INVALIDATE]: 'Invalidation',
};

export interface ICS2ErrorPointer {
    messageElementPath?: string;
}

export interface ICS2NotificationError {
    validationCode?: string;
    description?: string | null;
    technicalErrorMessage?: string | null;
    pointer?: ICS2ErrorPointer | null;
}

export interface ICS2TransportDocumentBody {
    documentNumber?: string;
    type?: string;
}

export interface ICS2DoNotLoadDetails {
    receptacle?: Array<{ receptacleIdentificationNumber?: string }> | null;
    transportDocument?: ICS2TransportDocumentBody | null;
    transportEquipment?: Array<{ containerIdentificationNumber?: string }> | null;
}

export interface ICS2NotificationBody {
    error?: ICS2NotificationError[] | null;
    functionalReference?: string | null;
    notificationDate?: { DateTime?: string; dateTime?: string } | null;
    documentIssueDate?: { DateTime?: string; dateTime?: string } | null;
    MRN?: string | null;
    mrn?: string | null;
    transportDocument?: ICS2TransportDocumentBody | null;
    transportDocumentHouse?: ICS2TransportDocumentBody | null;
    declarant?: { identificationNumber?: string } | null;
    representative?: { identificationNumber?: string } | null;
    carrier?: { identificationNumber?: string } | null;
    customsOfficeOfFirstEntry?: { referenceNumber?: string } | null;
    addressedMemberState?: { country?: string } | null;
    responsibleMemberState?: { country?: string } | null;
    doNotLoadDetails?: ICS2DoNotLoadDetails | null;
    referralRequestDetails?: Array<{ requestType?: string; referralRequestReference?: string }> | null;
}

// Note: To be tested once we have actual data (currently the BE seeds mock) and remove one of the mrn fields from the payload
export const readNotificationMrn = (body?: ICS2NotificationBody | null) => body?.MRN ?? body?.mrn ?? undefined;

// Note: To be tested once we have actual data (currently the BE seeds mock) and remove one of the fields from the payload
export const readNotificationIssueDate = (body?: ICS2NotificationBody | null) =>
    body?.documentIssueDate?.DateTime ??
    body?.documentIssueDate?.dateTime ??
    body?.notificationDate?.DateTime ??
    body?.notificationDate?.dateTime ??
    undefined;

export interface ICS2Notification {
    id?: string;
    notificationId?: string;
    transactionId?: string;
    messageType?: string;
    processingStatus?: ICS2NotificationProcessingStatus;
    ics2TransactionType?: ICS2TransactionType;
    createdAt?: string;
    notification?: ICS2NotificationBody;
    rawMessage?: string;
    additionalProperties?: unknown | null;
}

const ics2NotificationMessageLabels: Record<string, string> = {
    IE3N01: 'ENS lifecycle validation error notification',
    IE3N02: 'ENS Not complete notification',
    IE3N03: 'Assessment complete notification',
    IE3N06: 'Arrival notification',
    IE3N07: 'House consignment in incorrect state notification',
    IE3N08: 'Control notification',
    IE3N09: 'Authorised Economic Operator control notification',
    IE3N10: 'Amendment Notification',
    IE3N99: 'Error notification',
    IE3Q01: 'Do Not Load request',
    IE3Q02: 'Additional information request',
    IE3Q03: 'High Risk Cargo & Mail screening request',
    IE3R01: 'ENS Registration Response',
    IE3R04: 'Arrival Registration Response',
    IE3R07: 'Invalidation Acceptance Response',
    IE3R08: 'ENS Consultation results',
};

export const getIcs2NotificationMessageLabel = (messageType: string | undefined | null) =>
    messageType ? ics2NotificationMessageLabels[messageType] ?? messageType : undefined;

/**
 * How a notification reads in the Status History table.
 *
 * A referral request also says how many referrals it carries, because that is the size of the work
 * it represents - one IE3Q02 may hold anything from one to 999 of them, and the label alone gives
 * no hint which.
 */
export const getIcs2NotificationDescription = (notification: ICS2Notification | undefined) => {
    const label = getIcs2NotificationMessageLabel(notification?.messageType);
    const referrals = notification?.notification?.referralRequestDetails?.length ?? 0;
    if (!label || !referrals) return label;
    return `${label} - ${referrals} referral${referrals === 1 ? '' : 's'}`;
};
